import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowUpRight, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { CUISINES } from "@/lib/mockData";
import { fetchBlog } from "@/lib/apiData";

export default function Discovery() {
  const { data: posts = [] } = useQuery({ queryKey: ["blog"], queryFn: fetchBlog });
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  const cats = ["All", ...Array.from(new Set(posts.map((a) => a.category)))];
  const articles = posts.filter(
    (a) => (cat === "All" || a.category === cat) && (a.title.toLowerCase().includes(q.toLowerCase()) || (a.excerpt || "").toLowerCase().includes(q.toLowerCase()))
  );
  const [feature, ...rest] = articles;

  return (
    <div data-testid="discovery-page" className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-3">Stories & discovery</p>
        <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter text-neutral-900">Stories behind the flavour.</h1>
        <p className="mt-4 text-neutral-600 max-w-xl">Deep dives, origins, and conversations with the people cooking the world's boldest food.</p>
      </motion.div>

      <div className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between border-b border-neutral-200 pb-6">
        <div className="flex gap-2 flex-wrap">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`discovery-cat-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`text-sm px-4 py-2 transition-colors ${cat === c ? "bg-[#d00000] text-white" : "bg-white border border-neutral-300 text-neutral-700 hover:border-[#d00000]"}`}>
              {c}
            </button>
          ))}
        </div>
        <div className="relative max-w-xs w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search stories…" data-testid="discovery-search" className="h-11 pl-11 bg-white border-neutral-300 text-neutral-900 rounded-none focus-visible:ring-[#d00000]" />
        </div>
      </div>

      {feature && (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Link to={`/blog/${feature.id}`} className="group grid md:grid-cols-2 gap-8 mt-12 border border-neutral-200 bg-white overflow-hidden" data-testid="discovery-feature">
            <div className="relative overflow-hidden aspect-video md:aspect-auto min-h-[280px]">
              <img src={feature.cover} alt={feature.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <span className="text-xs uppercase tracking-[0.2em] text-[#d00000]">{feature.category} · {feature.read}</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-neutral-900 mt-3 leading-tight">{feature.title}</h2>
              <p className="text-neutral-600 mt-4 leading-relaxed">{feature.excerpt}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-[#d00000]">Read the story <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" /></span>
            </div>
          </Link>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
        {rest.map((a, i) => (
          <motion.div key={a.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
            <Link to={`/blog/${a.id}`} className="group block border border-neutral-200 bg-white hover:border-[#d00000] transition-colors h-full" data-testid={`discovery-article-${a.id}`}>
              <div className="relative overflow-hidden aspect-[16/10]">
                <img src={a.cover} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-6">
                <span className="text-xs uppercase tracking-[0.2em] text-[#d00000]">{a.category} · {a.read}</span>
                <h3 className="font-display text-xl font-bold text-neutral-900 mt-2 leading-tight">{a.title}</h3>
                <p className="text-sm text-neutral-600 mt-2">{a.excerpt}</p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      {articles.length === 0 && <p className="py-24 text-center text-neutral-400" data-testid="discovery-empty">No stories match your search.</p>}

      <section className="mt-24">
        <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-6">Explore by cuisine</p>
        <div className="flex gap-3 flex-wrap">
          {CUISINES.map((c) => (
            <Link key={c.id} to={`/marketplace?cuisine=${encodeURIComponent(c.name)}`} className="px-5 py-3 border border-neutral-300 text-neutral-700 hover:border-[#d00000] hover:text-[#d00000] transition-colors font-display text-lg">
              {c.name}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
